/**
 * Move Dialog - Folder selection for moving files
 *
 * Displays a folder tree for selecting the destination folder
 * when moving files/folders.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeNode, FileNode } from '@/types/files';
import { getFileTree, moveFile, bulkMoveFiles } from '@/services/filesApi';

interface MoveDialogProps {
  isOpen: boolean;
  files: FileNode[];
  onClose: () => void;
  onMoveComplete: () => void;
}

const MoveDialog: React.FC<MoveDialogProps> = ({
  isOpen,
  files,
  onClose,
  onMoveComplete,
}) => {
  const { t } = useTranslation('files');
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get IDs of files being moved (to exclude from selection)
  const movingIds = new Set(files.map(f => f.id));
  // Get parent IDs of files being moved (can't move to current location)
  const currentParentIds = new Set(files.map(f => f.parent_id));

  useEffect(() => {
    if (isOpen) {
      loadTree();
    }
  }, [isOpen]);

  const loadTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const treeData = await getFileTree(null, 10, false);
      setTree(treeData);
      // Expand all folders by default
      const allFolderIds = new Set<string>();
      const collectIds = (nodes: TreeNode[]) => {
        for (const node of nodes) {
          if (node.node.is_folder) {
            allFolderIds.add(node.node.id);
            if (node.children) {
              collectIds(node.children);
            }
          }
        }
      };
      collectIds(treeData);
      setExpandedFolders(allFolderIds);
    } catch (err) {
      setError(t('move_dialog.error_load_folders'));
      console.error('Failed to load tree:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleMove = async () => {
    setMoving(true);
    setError(null);
    try {
      if (files.length === 1) {
        await moveFile(files[0].id, { new_parent_id: selectedFolderId });
      } else {
        await bulkMoveFiles({
          file_ids: files.map(f => f.id),
          dest_parent_id: selectedFolderId,
        });
      }
      onMoveComplete();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('move_dialog.error_move'));
      console.error('Move failed:', err);
    } finally {
      setMoving(false);
    }
  };

  const renderTreeNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    const { node: fileNode, children } = node;

    // Skip if this is one of the files being moved
    if (movingIds.has(fileNode.id)) return null;

    // Only show folders
    if (!fileNode.is_folder) return null;

    const isExpanded = expandedFolders.has(fileNode.id);
    const isSelected = selectedFolderId === fileNode.id;
    const isSameAsParent = currentParentIds.has(fileNode.id);
    const hasChildren = children && children.some(c => c.node.is_folder && !movingIds.has(c.node.id));

    return (
      <div key={fileNode.id}>
        <div
          className={`
            flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer
            ${isSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}
            ${isSameAsParent ? 'opacity-50' : ''}
          `}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => !isSameAsParent && setSelectedFolderId(fileNode.id)}
        >
          {/* Expand/collapse toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(fileNode.id);
            }}
            className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {hasChildren ? (
              <svg
                className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <span className="w-3 h-3" />
            )}
          </button>

          {/* Folder icon */}
          <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>

          {/* Folder name */}
          <span className="truncate text-sm">{fileNode.name}</span>

          {isSameAsParent && (
            <span className="text-xs text-gray-400">({t('move_dialog.current')})</span>
          )}
        </div>

        {/* Children */}
        {isExpanded && children && (
          <div>
            {children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {files.length === 1 ? t('move_dialog.title_single', { name: files[0].name }) : t('move_dialog.title_multi', { count: files.length })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm text-gray-600 mb-3">{t('move_dialog.select_destination')}</p>

          {/* Root folder option */}
          <div
            className={`
              flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer mb-2
              ${selectedFolderId === null ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}
              ${currentParentIds.has(null as any) ? 'opacity-50' : ''}
            `}
            onClick={() => !currentParentIds.has(null as any) && setSelectedFolderId(null)}
          >
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-sm font-medium">{t('move_dialog.root_disk')}</span>
          </div>

          {/* Folder tree */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                {t('move_dialog.loading_folders')}
              </div>
            ) : tree.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {t('move_dialog.no_folders')}
              </div>
            ) : (
              <div className="py-2">
                {tree.map(node => renderTreeNode(node, 0))}
              </div>
            )}
          </div>

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            onClick={handleMove}
            disabled={moving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {moving ? t('move_dialog.moving') : t('move_dialog.move_here')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveDialog;
